import React, { useState, useEffect, useCallback } from 'react';
import IconPicker from './IconPicker';

const LearningAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [subTab, setSubTab] = useState('alphabet');
  const [alphabet, setAlphabet] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [phrases, setPhrases] = useState([]);
  const [customTabs, setCustomTabs] = useState([]);
  const [selectedCustomTab, setSelectedCustomTab] = useState(null);
  const [customTabItems, setCustomTabItems] = useState([]);
  const [showTabForm, setShowTabForm] = useState(false);
  const [tabForm, setTabForm] = useState({});
  const [editingTab, setEditingTab] = useState(null);
  const [settings, setSettings] = useState({ page_visible: true, tabs: {} });
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [iconPickerTarget, setIconPickerTarget] = useState(null); // 'alphabet', 'sentence_patterns', 'common_phrases', 'tab_form', or 'custom_<id>'

  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  const openIconPicker = (target) => {
    setIconPickerTarget(target);
    setIconPickerOpen(true);
  };

  const handleIconSelect = async (url) => {
    setIconPickerOpen(false);
    if (iconPickerTarget === 'tab_form') {
      setTabForm(f => ({ ...f, icon_url: url }));
      showNotification('Icon selected — save the tab to apply', 'info');
    } else if (iconPickerTarget && iconPickerTarget.startsWith('custom_')) {
      const tabId = iconPickerTarget.replace('custom_', '');
      try {
        await fetch(`${API}/admin/learning/custom-tabs/${tabId}`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ icon_url: url }) });
        setCustomTabs(prev => prev.map(t => t.id === tabId ? { ...t, icon_url: url } : t));
        showNotification('Tab icon saved!', 'success');
      } catch { showNotification('Failed to save icon', 'error'); }
    } else if (iconPickerTarget) {
      const updated = { ...settings, [iconPickerTarget]: url };
      setSettings(updated);
      try {
        await fetch(`${API}/admin/learning/settings`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ [iconPickerTarget]: url }) });
        showNotification('Tab icon saved!', 'success');
      } catch { showNotification('Failed to save icon', 'error'); }
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, pRes, phRes, sRes, ctRes] = await Promise.all([
        fetch(`${API}/admin/learning/alphabet`, { headers: authHeaders }),
        fetch(`${API}/admin/learning/sentence-patterns`, { headers: authHeaders }),
        fetch(`${API}/admin/learning/common-phrases`, { headers: authHeaders }),
        fetch(`${API}/admin/learning/settings`, { headers: authHeaders }),
        fetch(`${API}/admin/learning/custom-tabs`, { headers: authHeaders }),
      ]);
      if (aRes.ok) { const d = await aRes.json(); setAlphabet(d.items || d); }
      if (pRes.ok) { const d = await pRes.json(); setPatterns(d.items || d); }
      if (phRes.ok) { const d = await phRes.json(); setPhrases(d.items || d); }
      if (sRes.ok) setSettings(await sRes.json());
      if (ctRes.ok) { const d = await ctRes.json(); setCustomTabs(d.tabs || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, adminToken]);

  const loadCustomTabItems = useCallback(async (tabId) => {
    try {
      const res = await fetch(`${API}/admin/learning/custom-tabs/${tabId}/items`, { headers: authHeaders });
      if (res.ok) { const d = await res.json(); setCustomTabItems(d.items || []); }
    } catch (e) { console.error(e); }
  }, [API, adminToken]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/learning/settings`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify(settings)
      });
      if (res.ok) showNotification('Settings saved!', 'success');
    } catch (e) { showNotification('Save failed', 'error'); }
  };

  const getEndpoint = () => {
    if (subTab === 'alphabet') return 'alphabet';
    if (subTab === 'patterns') return 'sentence-patterns';
    if (subTab === 'custom') return null;
    return 'common-phrases';
  };

  const handleSave = async () => {
    if (subTab === 'custom' && selectedCustomTab) {
      const method = editItem ? 'PUT' : 'POST';
      const url = editItem
        ? `${API}/admin/learning/custom-tabs/${selectedCustomTab}/items/${editItem}`
        : `${API}/admin/learning/custom-tabs/${selectedCustomTab}/items`;
      try {
        const res = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(form) });
        if (res.ok) { showNotification('Saved!', 'success'); setShowForm(false); setEditItem(null); setForm({}); loadCustomTabItems(selectedCustomTab); }
        else showNotification('Save failed', 'error');
      } catch (e) { showNotification('Save failed', 'error'); }
      return;
    }
    const endpoint = getEndpoint();
    const method = editItem ? 'PUT' : 'POST';
    const url = editItem ? `${API}/admin/learning/${endpoint}/${editItem}` : `${API}/admin/learning/${endpoint}`;
    try {
      const res = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(form) });
      if (res.ok) { showNotification('Saved!', 'success'); setShowForm(false); setEditItem(null); setForm({}); loadData(); }
      else showNotification('Save failed', 'error');
    } catch (e) { showNotification('Save failed', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    if (subTab === 'custom' && selectedCustomTab) {
      try {
        const res = await fetch(`${API}/admin/learning/custom-tabs/${selectedCustomTab}/items/${id}`, { method: 'DELETE', headers: authHeaders });
        if (res.ok) { showNotification('Deleted!', 'success'); loadCustomTabItems(selectedCustomTab); }
      } catch (e) { showNotification('Delete failed', 'error'); }
      return;
    }
    const endpoint = getEndpoint();
    try {
      const res = await fetch(`${API}/admin/learning/${endpoint}/${id}`, { method: 'DELETE', headers: authHeaders });
      if (res.ok) { showNotification('Deleted!', 'success'); loadData(); }
    } catch (e) { showNotification('Delete failed', 'error'); }
  };

  const handleSaveTab = async () => {
    const method = editingTab ? 'PUT' : 'POST';
    const url = editingTab ? `${API}/admin/learning/custom-tabs/${editingTab}` : `${API}/admin/learning/custom-tabs`;
    try {
      const res = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(tabForm) });
      if (res.ok) {
        const data = await res.json();
        showNotification('Tab saved!', 'success');
        setShowTabForm(false); setEditingTab(null); setTabForm({});
        await loadData();
        // Auto-select the new tab so user can immediately add items
        if (!editingTab && data.id) {
          setSelectedCustomTab(data.id);
          loadCustomTabItems(data.id);
        }
      }
      else { const d = await res.json().catch(() => ({})); showNotification(d.detail || 'Save failed', 'error'); }
    } catch (e) { showNotification('Save failed', 'error'); }
  };

  const handleDeleteTab = async (tabId) => {
    if (!window.confirm('Delete this tab and ALL its items?')) return;
    try {
      const res = await fetch(`${API}/admin/learning/custom-tabs/${tabId}`, { method: 'DELETE', headers: authHeaders });
      if (res.ok) { showNotification('Tab deleted!', 'success'); if (selectedCustomTab === tabId) { setSelectedCustomTab(null); setCustomTabItems([]); } loadData(); }
    } catch (e) { showNotification('Delete failed', 'error'); }
  };

  const handleReorderTab = async (tabId, direction) => {
    try {
      const res = await fetch(`${API}/admin/learning/custom-tabs/${tabId}/reorder`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify({ direction })
      });
      if (res.ok) loadData();
    } catch (e) { console.error(e); }
  };

  const startEdit = (item) => {
    const { id, _id, ...rest } = item;
    setEditItem(id || _id);
    setForm(rest);
    setShowForm(true);
  };

  const startAdd = () => { setEditItem(null); setForm({}); setShowForm(true); };

  const handleReorderItem = async (itemId, direction) => {
    if (!selectedCustomTab) return;
    try {
      const res = await fetch(`${API}/admin/learning/custom-tabs/${selectedCustomTab}/items/${itemId}/reorder`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify({ direction })
      });
      if (res.ok) loadCustomTabItems(selectedCustomTab);
    } catch (e) { console.error(e); }
  };

  const currentItems = subTab === 'alphabet' ? alphabet : subTab === 'patterns' ? patterns : subTab === 'custom' ? customTabItems : phrases;

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textColor = isDarkMode ? 'text-white' : 'text-gray-800';
  const subTextColor = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  if (loading) return <div className="text-center py-8"><div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className={`text-lg font-bold ${textColor}`}>Learn Darija Management</h3>
        {subTab !== 'custom' && (
        <div className="flex gap-2">
          <button onClick={startAdd} className="px-3 py-1.5 text-xs rounded-lg bg-purple-600 text-white hover:bg-purple-700">+ Add Item</button>
        </div>
        )}
      </div>

      {/* Settings */}
      <div className={`rounded-lg border p-4 ${cardBg}`}>
        <h4 className={`font-semibold mb-3 ${textColor}`}>Settings</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={settings.page_visible !== false}
              onChange={(e) => setSettings(s => ({ ...s, page_visible: e.target.checked }))} />
            <span className={`text-sm ${textColor}`}>Page Visible</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={settings.audio_enabled !== false}
              onChange={(e) => setSettings(s => ({ ...s, audio_enabled: e.target.checked }))} />
            <span className={`text-sm ${textColor}`}>Audio Enabled (all tabs)</span>
          </label>
          {[
            { key: 'alphabet', label: 'Alphabet' },
            { key: 'sentence_patterns', label: 'Sentence Patterns' },
            { key: 'common_phrases', label: 'Common Phrases' },
            ...customTabs.map(ct => ({ key: `custom_${ct.id}`, label: ct.name || ct.key }))
          ].map(tab => (
            <div key={tab.key} className="flex items-center gap-3">
              <span className={`text-xs ${subTextColor} w-28 truncate`} title={tab.label}>{tab.label}</span>
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={settings.tabs?.[tab.key]?.enabled !== false}
                  onChange={(e) => setSettings(s => ({ ...s, tabs: { ...s.tabs, [tab.key]: { ...s.tabs?.[tab.key], enabled: e.target.checked } } }))} />
                <span className={`text-xs ${textColor}`}>On</span>
              </label>
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={settings.tabs?.[tab.key]?.is_pro_only === true}
                  onChange={(e) => setSettings(s => ({ ...s, tabs: { ...s.tabs, [tab.key]: { ...s.tabs?.[tab.key], is_pro_only: e.target.checked } } }))} />
                <span className={`text-xs ${textColor}`}>Pro</span>
              </label>
            </div>
          ))}
        </div>
        <button onClick={handleSaveSettings} className="mt-3 px-4 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700">Save Settings</button>

        {/* Base Tab Icons */}
        <div className="mt-4 pt-4 border-t border-gray-600">
          <h5 className={`text-sm font-semibold mb-3 ${textColor}`}>Tab Icons</h5>
          <div className="space-y-2">
            {[
              { key: 'alphabet_icon_url', label: 'Letters & Numbers', defaultIcon: 'أ' },
              { key: 'sentence_patterns_icon_url', label: 'Sentence Builder', defaultIcon: '🔧' },
              { key: 'common_phrases_icon_url', label: 'Common Patterns', defaultIcon: '💬' },
            ].map(tab => (
              <div key={tab.key} className="flex items-center gap-3">
                <span className={`text-xs w-28 truncate ${subTextColor}`}>{tab.label}</span>
                <div className="flex items-center gap-1">
                  {settings[tab.key] ? (
                    <>
                      <img src={`${API.replace('/api', '')}${settings[tab.key]}`} alt="" className="w-6 h-6 object-contain rounded" />
                      <button onClick={async () => {
                        const updated = { ...settings, [tab.key]: '' };
                        setSettings(updated);
                        try { await fetch(`${API}/admin/learning/settings`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ [tab.key]: '' }) }); showNotification('Icon cleared', 'success'); } catch {}
                      }} className="text-red-400 text-xs hover:text-red-300">✕</button>
                    </>
                  ) : <span className="text-lg w-6 text-center">{tab.defaultIcon}</span>}
                  <button onClick={() => openIconPicker(tab.key)} className={`px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>Pick</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 flex-wrap">
        {[{ id: 'alphabet', label: 'Alphabet' }, { id: 'patterns', label: 'Sentence Patterns' }, { id: 'phrases', label: 'Common Phrases' }, { id: 'custom', label: 'Custom Tabs' }].map(t => (
          <button key={t.id} onClick={() => { setSubTab(t.id); setShowForm(false); setShowTabForm(false); if (t.id !== 'custom') { setSelectedCustomTab(null); } }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${subTab === t.id ? 'bg-purple-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {t.label} ({t.id === 'alphabet' ? alphabet.length : t.id === 'patterns' ? patterns.length : t.id === 'custom' ? customTabs.length : phrases.length})
          </button>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <div className={`rounded-lg border p-4 ${cardBg}`}>
          <h4 className={`font-semibold mb-3 ${textColor}`}>{editItem ? 'Edit' : 'Add'} {subTab === 'alphabet' ? 'Letter' : subTab === 'patterns' ? 'Pattern' : subTab === 'custom' ? 'Item' : 'Phrase'}</h4>
          {subTab === 'alphabet' && (() => {
            const gradientOptions = [
              { id: 'purple-pink', label: 'Purple Pink', icon: 'from-purple-400 to-pink-400' },
              { id: 'blue-cyan', label: 'Blue Cyan', icon: 'from-blue-400 to-cyan-300' },
              { id: 'emerald-teal', label: 'Emerald Teal', icon: 'from-emerald-400 to-teal-300' },
              { id: 'orange-amber', label: 'Orange Amber', icon: 'from-orange-400 to-amber-300' },
              { id: 'rose-red', label: 'Rose Red', icon: 'from-rose-400 to-red-400' },
              { id: 'violet-indigo', label: 'Violet Indigo', icon: 'from-violet-400 to-indigo-400' },
              { id: 'sky-blue', label: 'Sky Blue', icon: 'from-sky-300 to-blue-400' },
              { id: 'lime-green', label: 'Lime Green', icon: 'from-lime-400 to-green-400' },
              { id: 'fuchsia-purple', label: 'Fuchsia Purple', icon: 'from-fuchsia-400 to-purple-500' },
              { id: 'amber-yellow', label: 'Amber Yellow', icon: 'from-amber-400 to-yellow-300' },
              { id: 'teal-cyan', label: 'Teal Cyan', icon: 'from-teal-400 to-cyan-400' },
              { id: 'pink-rose', label: 'Pink Rose', icon: 'from-pink-300 to-rose-400' },
              { id: 'indigo-blue', label: 'Indigo Blue', icon: 'from-indigo-400 to-blue-300' },
              { id: 'red-orange', label: 'Red Orange', icon: 'from-red-400 to-orange-300' },
              { id: 'cyan-emerald', label: 'Cyan Emerald', icon: 'from-cyan-400 to-emerald-400' },
              { id: 'yellow-lime', label: 'Yellow Lime', icon: 'from-yellow-300 to-lime-400' },
              { id: 'purple-blue', label: 'Purple Blue', icon: 'from-purple-500 to-blue-400' },
              { id: 'green-emerald', label: 'Green Emerald', icon: 'from-green-400 to-emerald-300' },
              { id: 'slate-gray', label: 'Slate Gray', icon: 'from-slate-400 to-gray-400' },
              { id: 'rose-pink', label: 'Rose Pink', icon: 'from-rose-300 to-pink-400' },
            ];
            return (
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Symbol (e.g. 7)" value={form.symbol || ''} onChange={e => setForm(f => ({ ...f, symbol: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <input placeholder="Arabic letter (e.g. ح)" value={form.arabic_letter || ''} onChange={e => setForm(f => ({ ...f, arabic_letter: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <input placeholder="Name" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <select value={form.difficulty || 'beginner'} onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
              </select>
              <input placeholder="Category (e.g. Special Darija Letters, Full Alphabet)" value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              {/* Gradient Color Picker */}
              <div className="col-span-2 border rounded-lg p-3" style={{ borderColor: isDarkMode ? '#555' : '#ddd' }}>
                <p className={`text-xs font-semibold mb-2 ${textColor}`}>Card Color (Icon & Background)</p>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                  {gradientOptions.map(g => (
                    <button key={g.id} type="button" onClick={() => setForm(f => ({ ...f, gradient: g.id }))}
                      className={`w-full aspect-square rounded-lg bg-gradient-to-br ${g.icon} transition-all ${form.gradient === g.id ? 'ring-2 ring-offset-2 ring-purple-500 scale-110' : 'hover:scale-105'}`}
                      title={g.label} />
                  ))}
                </div>
                {form.gradient && <p className={`text-xs mt-2 ${subTextColor}`}>Selected: {gradientOptions.find(g => g.id === form.gradient)?.label || form.gradient}</p>}
              </div>
              <textarea placeholder="Pronunciation" value={form.pronunciation || ''} onChange={e => setForm(f => ({ ...f, pronunciation: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} rows={2} />
              <textarea placeholder="Tips" value={form.tips || ''} onChange={e => setForm(f => ({ ...f, tips: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} rows={2} />
              <input placeholder="Audio URL" value={form.audio_url || ''} onChange={e => setForm(f => ({ ...f, audio_url: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              {/* Examples management */}
              <div className="col-span-2 border rounded-lg p-3" style={{ borderColor: isDarkMode ? '#555' : '#ddd' }}>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-xs font-semibold ${textColor}`}>Examples</p>
                  <button type="button" onClick={() => setForm(f => ({ ...f, examples: [...(f.examples || []), { word: '', arabic: '', english: '', audio_url: '' }] }))}
                    className="px-2 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700">+ Add Example</button>
                </div>
                {(form.examples || []).map((ex, ei) => (
                  <div key={ei} className="mb-2 p-2 rounded-lg border" style={{ borderColor: isDarkMode ? '#555' : '#ddd', backgroundColor: isDarkMode ? '#1e1e2e' : '#f9fafb' }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${subTextColor}`}>Example {ei + 1}</span>
                      <button type="button" onClick={() => setForm(f => ({ ...f, examples: f.examples.filter((_, i) => i !== ei) }))}
                        className="px-1.5 py-0.5 text-xs rounded bg-red-600 text-white hover:bg-red-700">Remove</button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input placeholder="Latin (e.g. 7lib)" value={ex.word || ex.darija || ''} onChange={e => setForm(f => ({ ...f, examples: f.examples.map((x, i) => i === ei ? { ...x, word: e.target.value } : x) }))}
                        className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                      <input placeholder="Arabic script (e.g. حليب)" value={ex.arabic || ''} onChange={e => setForm(f => ({ ...f, examples: f.examples.map((x, i) => i === ei ? { ...x, arabic: e.target.value } : x) }))}
                        className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} style={{ direction: 'rtl' }} />
                      <input placeholder="English (e.g. milk)" value={ex.english || ''} onChange={e => setForm(f => ({ ...f, examples: f.examples.map((x, i) => i === ei ? { ...x, english: e.target.value } : x) }))}
                        className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                      <input placeholder="Audio URL" value={ex.audio_url || ''} onChange={e => setForm(f => ({ ...f, examples: f.examples.map((x, i) => i === ei ? { ...x, audio_url: e.target.value } : x) }))}
                        className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                    </div>
                  </div>
                ))}
                {(!form.examples || form.examples.length === 0) && (
                  <p className={`text-xs text-center py-2 ${subTextColor}`}>No examples yet. Click "+ Add Example" to add one.</p>
                )}
              </div>
            </div>
          ); })()}
          {subTab === 'patterns' && (
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Title" value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <input placeholder="Subtitle" value={form.subtitle || ''} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <input placeholder="Formula (e.g. ka + verb)" value={form.formula || ''} onChange={e => setForm(f => ({ ...f, formula: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <textarea placeholder="Explanation" value={form.explanation || ''} onChange={e => setForm(f => ({ ...f, explanation: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} rows={2} />
              <input placeholder="Breakdown (e.g. ka+n+akol)" value={form.breakdown || ''} onChange={e => setForm(f => ({ ...f, breakdown: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <select value={form.difficulty || 'beginner'} onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
              </select>
              {/* Examples management */}
              <div className="col-span-2 border rounded-lg p-3" style={{ borderColor: isDarkMode ? '#555' : '#ddd' }}>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-xs font-semibold ${textColor}`}>Examples</p>
                  <button type="button" onClick={() => setForm(f => ({ ...f, examples: [...(f.examples || []), { darija: '', arabic: '', english: '' }] }))}
                    className="px-2 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700">+ Add Example</button>
                </div>
                {(form.examples || []).map((ex, ei) => (
                  <div key={ei} className="mb-2 p-2 rounded-lg border" style={{ borderColor: isDarkMode ? '#555' : '#ddd', backgroundColor: isDarkMode ? '#1e1e2e' : '#f9fafb' }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${subTextColor}`}>Example {ei + 1}</span>
                      <button type="button" onClick={() => setForm(f => ({ ...f, examples: f.examples.filter((_, i) => i !== ei) }))}
                        className="px-1.5 py-0.5 text-xs rounded bg-red-600 text-white hover:bg-red-700">Remove</button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <input placeholder="Latin (e.g. ghadi nakol)" value={ex.darija || ''} onChange={e => setForm(f => ({ ...f, examples: f.examples.map((x, i) => i === ei ? { ...x, darija: e.target.value } : x) }))}
                        className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                      <input placeholder="Arabic script (e.g. غادي ناكول)" value={ex.arabic || ''} onChange={e => setForm(f => ({ ...f, examples: f.examples.map((x, i) => i === ei ? { ...x, arabic: e.target.value } : x) }))}
                        className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} style={{ direction: 'rtl' }} />
                      <input placeholder="English (e.g. I will eat)" value={ex.english || ''} onChange={e => setForm(f => ({ ...f, examples: f.examples.map((x, i) => i === ei ? { ...x, english: e.target.value } : x) }))}
                        className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                    </div>
                  </div>
                ))}
                {(!form.examples || form.examples.length === 0) && (
                  <p className={`text-xs text-center py-2 ${subTextColor}`}>No examples yet. Click "+ Add Example" to add one.</p>
                )}
              </div>
            </div>
          )}
          {subTab === 'phrases' && (
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="English template (e.g. I want...)" value={form.english_template || ''} onChange={e => setForm(f => ({ ...f, english_template: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <input placeholder="Darija template (e.g. bghit...)" value={form.darija_template || ''} onChange={e => setForm(f => ({ ...f, darija_template: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <input placeholder="Arabic script (e.g. بغيت...)" value={form.arabic_template || ''} onChange={e => setForm(f => ({ ...f, arabic_template: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} style={{ direction: 'rtl' }} />
              <input placeholder="Pronunciation" value={form.pronunciation || ''} onChange={e => setForm(f => ({ ...f, pronunciation: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <select value={form.difficulty || 'beginner'} onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
              </select>
            </div>
          )}
          {subTab === 'custom' && selectedCustomTab && (() => {
            const gradientOptions = [
              { id: 'purple-pink', label: 'Purple Pink', icon: 'from-purple-400 to-pink-400' },
              { id: 'blue-cyan', label: 'Blue Cyan', icon: 'from-blue-400 to-cyan-300' },
              { id: 'emerald-teal', label: 'Emerald Teal', icon: 'from-emerald-400 to-teal-300' },
              { id: 'orange-amber', label: 'Orange Amber', icon: 'from-orange-400 to-amber-300' },
              { id: 'rose-red', label: 'Rose Red', icon: 'from-rose-400 to-red-400' },
              { id: 'violet-indigo', label: 'Violet Indigo', icon: 'from-violet-400 to-indigo-400' },
              { id: 'sky-blue', label: 'Sky Blue', icon: 'from-sky-300 to-blue-400' },
              { id: 'lime-green', label: 'Lime Green', icon: 'from-lime-400 to-green-400' },
              { id: 'fuchsia-purple', label: 'Fuchsia Purple', icon: 'from-fuchsia-400 to-purple-500' },
              { id: 'amber-yellow', label: 'Amber Yellow', icon: 'from-amber-400 to-yellow-300' },
              { id: 'teal-cyan', label: 'Teal Cyan', icon: 'from-teal-400 to-cyan-400' },
              { id: 'pink-rose', label: 'Pink Rose', icon: 'from-pink-300 to-rose-400' },
              { id: 'indigo-blue', label: 'Indigo Blue', icon: 'from-indigo-400 to-blue-300' },
              { id: 'red-orange', label: 'Red Orange', icon: 'from-red-400 to-orange-300' },
              { id: 'cyan-emerald', label: 'Cyan Emerald', icon: 'from-cyan-400 to-emerald-400' },
              { id: 'yellow-lime', label: 'Yellow Lime', icon: 'from-yellow-300 to-lime-400' },
              { id: 'purple-blue', label: 'Purple Blue', icon: 'from-purple-500 to-blue-400' },
              { id: 'green-emerald', label: 'Green Emerald', icon: 'from-green-400 to-emerald-300' },
              { id: 'slate-gray', label: 'Slate Gray', icon: 'from-slate-400 to-gray-400' },
              { id: 'rose-pink', label: 'Rose Pink', icon: 'from-rose-300 to-pink-400' },
            ];
            return (
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="English (e.g. Are you with me?)" value={form.english || ''} onChange={e => setForm(f => ({ ...f, english: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <input placeholder="Darija Latin (e.g. wach anta m3aya?)" value={form.darija_latin || ''} onChange={e => setForm(f => ({ ...f, darija_latin: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <input placeholder="Arabic (e.g. واش انت معايا)" value={form.arabic || ''} onChange={e => setForm(f => ({ ...f, arabic: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} style={{ direction: 'rtl' }} />
              <input placeholder="Note (e.g. talking to female)" value={form.note || ''} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              <select value={form.gender || ''} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                <option value="">No Gender</option><option value="female">Female</option><option value="male">Male</option>
              </select>
              <select value={form.number || ''} onChange={e => setForm(f => ({ ...f, number: e.target.value }))}
                className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                <option value="">No Number</option><option value="singular">Singular</option><option value="plural">Plural</option>
              </select>
              <select value={form.difficulty || 'beginner'} onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
              </select>
              <input placeholder="Audio URL (Dropbox link)" value={form.audio_url || ''} onChange={e => setForm(f => ({ ...f, audio_url: e.target.value }))}
                className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
              {/* Card Color Picker */}
              <div className="col-span-2 border rounded-lg p-3" style={{ borderColor: isDarkMode ? '#555' : '#ddd' }}>
                <p className={`text-xs font-semibold mb-2 ${textColor}`}>Card Color</p>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                  {gradientOptions.map(g => (
                    <button key={g.id} type="button" onClick={() => setForm(f => ({ ...f, gradient: g.id }))}
                      className={`w-full aspect-square rounded-lg bg-gradient-to-br ${g.icon} transition-all ${form.gradient === g.id ? 'ring-2 ring-offset-2 ring-purple-500 scale-110' : 'hover:scale-105'}`}
                      title={g.label} />
                  ))}
                </div>
                {form.gradient && <p className={`text-xs mt-2 ${subTextColor}`}>Selected: {gradientOptions.find(g => g.id === form.gradient)?.label || form.gradient}</p>}
              </div>
            </div>
            );
          })()}
          <div className="flex gap-2 mt-3">
            <button onClick={handleSave} className="px-4 py-1.5 text-xs rounded-lg bg-purple-600 text-white hover:bg-purple-700">Save</button>
            <button onClick={() => { setShowForm(false); setEditItem(null); }} className="px-4 py-1.5 text-xs rounded-lg bg-gray-500 text-white hover:bg-gray-600">Cancel</button>
          </div>
        </div>
      )}

      {/* Custom Tabs management */}
      {subTab === 'custom' && (
        <div className="space-y-3">
          {/* Create/Edit Tab Form */}
          {showTabForm && (
            <div className={`rounded-lg border p-4 ${cardBg}`}>
              <h4 className={`font-semibold mb-3 ${textColor}`}>{editingTab ? 'Edit Tab' : 'Create New Tab'}</h4>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Tab Name (e.g. Personal Pronouns)" value={tabForm.name || ''} onChange={e => setTabForm(f => ({ ...f, name: e.target.value }))}
                  className={`col-span-2 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                <div className="flex items-center gap-2">
                  {tabForm.icon_url ? (
                    <>
                      <img src={`${API.replace('/api', '')}${tabForm.icon_url}`} alt="" className="w-8 h-8 object-contain rounded" />
                      <button onClick={() => setTabForm(f => ({ ...f, icon_url: '' }))} className="text-red-400 text-xs hover:text-red-300">✕</button>
                    </>
                  ) : (
                    <input placeholder="Icon (emoji)" value={tabForm.icon || ''} onChange={e => setTabForm(f => ({ ...f, icon: e.target.value }))}
                      className={`w-20 px-2 py-2 rounded-lg border text-sm text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                  )}
                  <button onClick={() => openIconPicker('tab_form')} className={`px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>Pick</button>
                </div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={tabForm.is_pro_only === true} onChange={e => setTabForm(f => ({ ...f, is_pro_only: e.target.checked }))} />
                  <span className={`text-sm ${textColor}`}>Pro Only</span>
                </label>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={handleSaveTab} className="px-4 py-1.5 text-xs rounded-lg bg-purple-600 text-white hover:bg-purple-700">Save Tab</button>
                <button onClick={() => { setShowTabForm(false); setEditingTab(null); setTabForm({}); }} className="px-4 py-1.5 text-xs rounded-lg bg-gray-500 text-white hover:bg-gray-600">Cancel</button>
              </div>
            </div>
          )}

          {/* Tab list */}
          <div className="flex items-center justify-between">
            <h4 className={`font-semibold text-sm ${textColor}`}>Your Custom Tabs ({customTabs.length})</h4>
            <button onClick={() => { setShowTabForm(true); setEditingTab(null); setTabForm({}); }} className="px-3 py-1.5 text-xs rounded-lg bg-green-600 text-white hover:bg-green-700">+ Create Tab</button>
          </div>

          {customTabs.length === 0 && !showTabForm && (
            <div className={`text-center py-8 ${subTextColor}`}>No custom tabs yet. Click "+ Create Tab" to make one.</div>
          )}

          {/* Each tab as expandable section */}
          {customTabs.map((tab, tabIdx) => {
            const isOpen = selectedCustomTab === tab.id;
            return (
              <div key={tab.id} className={`rounded-lg border overflow-hidden ${cardBg} ${isOpen ? 'ring-2 ring-purple-500' : ''}`}>
                {/* Tab header — click to open/close */}
                <div className={`flex items-center justify-between p-3 cursor-pointer ${isOpen ? (isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50') : ''}`}
                  onClick={() => { if (isOpen) { setSelectedCustomTab(null); setCustomTabItems([]); setShowForm(false); } else { setSelectedCustomTab(tab.id); loadCustomTabItems(tab.id); setShowForm(false); } }}>
                  <div className="flex items-center gap-2 flex-1">
                    {tab.icon_url
                      ? <img src={`${API.replace('/api', '')}${tab.icon_url}`} alt="" className="w-6 h-6 object-contain" />
                      : <span className="text-lg">{tab.icon || '📝'}</span>}
                    <div>
                      <span className={`font-semibold text-sm ${textColor}`}>{tab.name}</span>
                      <span className={`ml-2 text-xs ${subTextColor}`}>{tab.item_count || 0} items</span>
                      {tab.is_pro_only && <span className="ml-2 text-xs text-amber-500 font-medium">PRO</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col gap-0.5" onClick={e => e.stopPropagation()}>
                      <button onClick={() => handleReorderTab(tab.id, 'up')} disabled={tabIdx === 0}
                        className={`px-1 py-0.5 text-[10px] rounded ${tabIdx === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-300 dark:hover:bg-gray-600'}`}
                        title="Move tab up">▲</button>
                      <button onClick={() => handleReorderTab(tab.id, 'down')} disabled={tabIdx === customTabs.length - 1}
                        className={`px-1 py-0.5 text-[10px] rounded ${tabIdx === customTabs.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-300 dark:hover:bg-gray-600'}`}
                        title="Move tab down">▼</button>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); openIconPicker(`custom_${tab.id}`); }}
                      className={`px-2 py-1 text-xs rounded font-medium ${isDarkMode ? 'bg-purple-700 text-white hover:bg-purple-600' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}>Pick</button>
                    <button onClick={(e) => { e.stopPropagation(); setEditingTab(tab.id); setTabForm({ name: tab.name, icon: tab.icon, icon_url: tab.icon_url || '', is_pro_only: tab.is_pro_only, enabled: tab.enabled }); setShowTabForm(true); }}
                      className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700">Edit</button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteTab(tab.id); }}
                      className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700">Del</button>
                    <span className={`text-sm transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ color: isDarkMode ? '#aaa' : '#666' }}>▼</span>
                  </div>
                </div>

                {/* Expanded: items list + add button */}
                {isOpen && (
                  <div className={`border-t p-3 space-y-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-xs font-medium ${subTextColor}`}>Items in this tab</p>
                      <button onClick={startAdd} className="px-3 py-1.5 text-xs rounded-lg bg-purple-600 text-white hover:bg-purple-700">+ Add Item</button>
                    </div>

                    {customTabItems.map((item, idx) => (
                      <div key={item.id || idx} className={`rounded-lg border p-3 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={`font-semibold text-sm ${textColor}`}>{item.english}</p>
                            <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{item.darija_latin}</p>
                            {item.arabic && <p className={`text-sm mt-0.5 ${subTextColor}`} style={{ fontFamily: 'serif', direction: 'rtl' }}>{item.arabic}</p>}
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              {item.gender && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${item.gender === 'female' ? (isDarkMode ? 'bg-pink-900/50 text-pink-300' : 'bg-pink-100 text-pink-700') : (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700')}`}>{item.gender === 'female' ? '♀ Female' : '♂ Male'}</span>}
                              {item.number && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${item.number === 'plural' ? (isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700') : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>{item.number === 'plural' ? 'Plural' : 'Singular'}</span>}
                              {item.note && <span className={`text-[10px] italic ${subTextColor}`}>— {item.note}</span>}
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0 items-center">
                            <div className="flex flex-col gap-0.5 mr-1">
                              <button onClick={() => handleReorderItem(item.id, 'up')} disabled={idx === 0}
                                className={`px-1 py-0.5 text-[10px] rounded ${idx === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-300 dark:hover:bg-gray-600'}`}
                                title="Move up">▲</button>
                              <button onClick={() => handleReorderItem(item.id, 'down')} disabled={idx === customTabItems.length - 1}
                                className={`px-1 py-0.5 text-[10px] rounded ${idx === customTabItems.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-300 dark:hover:bg-gray-600'}`}
                                title="Move down">▼</button>
                            </div>
                            <button onClick={() => startEdit(item)} className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700">Edit</button>
                            <button onClick={() => handleDelete(item.id)} className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700">Del</button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {customTabItems.length === 0 && (
                      <div className={`text-center py-6 rounded-lg border ${isDarkMode ? 'border-gray-700 border-dashed' : 'border-gray-200 border-dashed'}`}>
                        <p className={`text-sm ${subTextColor}`}>No items yet</p>
                        <p className={`text-xs mt-1 ${subTextColor}`}>Click "+ Add Item" above to start adding content</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Items list (for non-custom tabs) */}
      {subTab !== 'custom' && (
      <div className="space-y-2">
        {currentItems.map((item, idx) => (
          <div key={item.id || item._id || idx} className={`rounded-lg border p-3 flex items-center justify-between ${cardBg}`}>
            <div>
              <span className={`font-semibold text-sm ${textColor}`}>
                {subTab === 'alphabet' ? `${item.symbol} (${item.arabic_letter || ''}) - ${item.name}${item.category ? ` [${item.category}]` : ''}` :
                 subTab === 'patterns' ? item.title :
                 `"${item.english_template}" → ${item.darija_template}`}
              </span>
              {subTab === 'alphabet' && <span className={`ml-2 text-xs ${subTextColor}`}>{item.difficulty}</span>}
            </div>
            <div className="flex gap-1">
              <button onClick={() => startEdit(item)} className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700">Edit</button>
              <button onClick={() => handleDelete(item.id || item._id)} className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700">Del</button>
            </div>
          </div>
        ))}
        {currentItems.length === 0 && (
          <div className={`text-center py-8 ${subTextColor}`}>No items yet. Click "+ Add Item" to create one.</div>
        )}
      </div>
      )}
      {/* Icon Picker Modal */}
      {iconPickerOpen && (
        <IconPicker
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          onClose={() => setIconPickerOpen(false)}
          onPick={(url) => handleIconSelect(url)}
        />
      )}
    </div>
  );
};

export default LearningAdmin;
